import { useState, useEffect } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, Wallet, CreditCard, Building2, Banknote, Settings2, DollarSign, Loader2 } from 'lucide-react';

export default function PaymentSettingsPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [liveRate, setLiveRate] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/payment-settings'),
      api.get('/exchange-rate').catch(() => ({ data: { usdt_inr: 0 } }))
    ]).then(([cRes, rRes]) => {
      setConfig(cRes.data);
      setLiveRate(rRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/payment-settings', config);
      toast.success('Payment settings saved successfully');
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const set = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));

  if (loading || !config) return <div className="flex items-center justify-center h-64"><Loader2 size={24} className="animate-spin text-brand-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="payment-settings-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-brand-text-primary tracking-tight">Payment & Currency Settings</h1>
          <p className="text-sm text-brand-text-secondary mt-1">Configure payment methods, company accounts, and exchange rates</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-lg" data-testid="save-payment-settings-btn">
          {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />} Save Settings
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Crypto BEP20 */}
        <Card className="border border-brand-border bg-white">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2"><Wallet size={16} className="text-brand-primary" /> Crypto — USDT BEP20</CardTitle>
              <Switch checked={config.crypto_bep20_enabled} onCheckedChange={v => set('crypto_bep20_enabled', v)} data-testid="toggle-bep20" />
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Wallet Address</Label><Input value={config.crypto_bep20_address || ''} onChange={e => set('crypto_bep20_address', e.target.value)} placeholder="0x..." className="bg-white border-brand-border rounded-lg text-sm font-mono" data-testid="bep20-address" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Network Label</Label><Input value={config.crypto_bep20_network || ''} onChange={e => set('crypto_bep20_network', e.target.value)} className="bg-white border-brand-border rounded-lg text-sm" /></div>
          </CardContent>
        </Card>

        {/* Crypto TRC20 */}
        <Card className="border border-brand-border bg-white">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2"><Wallet size={16} className="text-brand-secondary" /> Crypto — USDT TRC20</CardTitle>
              <Switch checked={config.crypto_trc20_enabled} onCheckedChange={v => set('crypto_trc20_enabled', v)} data-testid="toggle-trc20" />
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Wallet Address</Label><Input value={config.crypto_trc20_address || ''} onChange={e => set('crypto_trc20_address', e.target.value)} placeholder="T..." className="bg-white border-brand-border rounded-lg text-sm font-mono" data-testid="trc20-address" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Network Label</Label><Input value={config.crypto_trc20_network || ''} onChange={e => set('crypto_trc20_network', e.target.value)} className="bg-white border-brand-border rounded-lg text-sm" /></div>
          </CardContent>
        </Card>

        {/* UPI */}
        <Card className="border border-brand-border bg-white">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2"><CreditCard size={16} className="text-brand-accent" /> UPI Payment</CardTitle>
              <Switch checked={config.upi_enabled} onCheckedChange={v => set('upi_enabled', v)} data-testid="toggle-upi" />
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">UPI ID</Label><Input value={config.upi_id || ''} onChange={e => set('upi_id', e.target.value)} placeholder="company@upi" className="bg-white border-brand-border rounded-lg text-sm" data-testid="upi-id" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Account Name</Label><Input value={config.upi_name || ''} onChange={e => set('upi_name', e.target.value)} placeholder="QUANTORA ANALYTICS" className="bg-white border-brand-border rounded-lg text-sm" data-testid="upi-name" /></div>
          </CardContent>
        </Card>

        {/* Bank Transfer */}
        <Card className="border border-brand-border bg-white">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2"><Building2 size={16} className="text-brand-success" /> Bank Transfer</CardTitle>
              <Switch checked={config.bank_transfer_enabled} onCheckedChange={v => set('bank_transfer_enabled', v)} data-testid="toggle-bank" />
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Bank Name</Label><Input value={config.bank_name || ''} onChange={e => set('bank_name', e.target.value)} placeholder="State Bank of India" className="bg-white border-brand-border rounded-lg text-sm" data-testid="bank-name" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Account Holder Name</Label><Input value={config.bank_account_name || ''} onChange={e => set('bank_account_name', e.target.value)} placeholder="QUANTORA ANALYTICS PVT LTD" className="bg-white border-brand-border rounded-lg text-sm" data-testid="bank-acc-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Account Number</Label><Input value={config.bank_account_no || ''} onChange={e => set('bank_account_no', e.target.value)} className="bg-white border-brand-border rounded-lg text-sm font-mono" data-testid="bank-acc-no" /></div>
              <div className="space-y-1.5"><Label className="text-xs">IFSC Code</Label><Input value={config.bank_ifsc || ''} onChange={e => set('bank_ifsc', e.target.value.toUpperCase())} className="bg-white border-brand-border rounded-lg text-sm font-mono" data-testid="bank-ifsc" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Branch</Label><Input value={config.bank_branch || ''} onChange={e => set('bank_branch', e.target.value)} className="bg-white border-brand-border rounded-lg text-sm" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Account Type</Label>
                <Select value={config.bank_account_type || 'Current'} onValueChange={v => set('bank_account_type', v)}>
                  <SelectTrigger className="bg-white border-brand-border rounded-lg text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Current">Current</SelectItem><SelectItem value="Savings">Savings</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Branch / Other Methods */}
      <Card className="border border-brand-border bg-white">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Banknote size={16} className="text-brand-text-primary" /> Other Payment Methods</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center justify-between bg-brand-bg/60 rounded-lg p-3 border border-brand-border/50">
              <div><p className="text-sm font-medium text-brand-text-primary">Cheque</p><p className="text-xs text-brand-text-secondary">Accept cheque deposits</p></div>
              <Switch checked={config.cheque_enabled} onCheckedChange={v => set('cheque_enabled', v)} data-testid="toggle-cheque" />
            </div>
            <div className="flex items-center justify-between bg-brand-bg/60 rounded-lg p-3 border border-brand-border/50">
              <div><p className="text-sm font-medium text-brand-text-primary">Cash</p><p className="text-xs text-brand-text-secondary">Branch cash deposit</p></div>
              <Switch checked={config.cash_enabled} onCheckedChange={v => set('cash_enabled', v)} data-testid="toggle-cash" />
            </div>
            <div className="flex items-center justify-between bg-brand-bg/60 rounded-lg p-3 border border-brand-border/50">
              <div><p className="text-sm font-medium text-brand-text-primary">Branch Deposit</p><p className="text-xs text-brand-text-secondary">In-person branch</p></div>
              <Switch checked={config.branch_deposit_enabled} onCheckedChange={v => set('branch_deposit_enabled', v)} data-testid="toggle-branch-deposit" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Currency & Exchange Rate */}
      <Card className="border border-brand-border bg-white">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><DollarSign size={16} className="text-brand-primary" /> Currency & Exchange Rate</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0 space-y-4">
          {liveRate && (
            <div className="bg-brand-bg/60 rounded-lg p-3 border border-brand-border/50">
              <p className="text-xs text-brand-text-secondary">Live Market Rate (CoinGecko)</p>
              <p className="font-heading text-lg font-bold text-brand-primary">1 USDT = ₹{liveRate.usdt_inr}</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label className="text-xs">Default Currency</Label>
              <Select value={config.default_currency || 'USDT'} onValueChange={v => set('default_currency', v)}>
                <SelectTrigger className="bg-white border-brand-border rounded-lg text-sm" data-testid="default-currency"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="USDT">USDT</SelectItem><SelectItem value="INR">INR</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between bg-brand-bg/60 rounded-lg p-3 border border-brand-border/50">
              <div><p className="text-sm font-medium text-brand-text-primary">INR Enabled</p><p className="text-xs text-brand-text-secondary">Allow INR deposits</p></div>
              <Switch checked={config.inr_enabled} onCheckedChange={v => set('inr_enabled', v)} data-testid="toggle-inr" />
            </div>
            <div className="flex items-center justify-between bg-brand-bg/60 rounded-lg p-3 border border-brand-border/50">
              <div><p className="text-sm font-medium text-brand-text-primary">Live Rate</p><p className="text-xs text-brand-text-secondary">Use CoinGecko</p></div>
              <Switch checked={config.use_live_rate} onCheckedChange={v => set('use_live_rate', v)} data-testid="toggle-live-rate" />
            </div>
          </div>
          {!config.use_live_rate && (
            <div className="space-y-1.5"><Label className="text-xs">Manual Exchange Rate (1 USDT = ₹ ?)</Label><Input type="number" value={config.manual_exchange_rate || ''} onChange={e => set('manual_exchange_rate', parseFloat(e.target.value) || 0)} placeholder="83.00" className="bg-white border-brand-border rounded-lg text-sm max-w-xs" data-testid="manual-rate" /></div>
          )}
        </CardContent>
      </Card>

      {/* Limits */}
      <Card className="border border-brand-border bg-white">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Settings2 size={16} /> Transaction Limits</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Min Deposit ($)</Label><Input type="number" value={config.min_deposit || ''} onChange={e => set('min_deposit', parseFloat(e.target.value) || 0)} className="bg-white border-brand-border rounded-lg text-sm" data-testid="min-deposit" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Max Deposit ($)</Label><Input type="number" value={config.max_deposit || ''} onChange={e => set('max_deposit', parseFloat(e.target.value) || 0)} className="bg-white border-brand-border rounded-lg text-sm" data-testid="max-deposit" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Min Withdrawal ($)</Label><Input type="number" value={config.min_withdrawal || ''} onChange={e => set('min_withdrawal', parseFloat(e.target.value) || 0)} className="bg-white border-brand-border rounded-lg text-sm" data-testid="min-withdrawal" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Max Withdrawal ($)</Label><Input type="number" value={config.max_withdrawal || ''} onChange={e => set('max_withdrawal', parseFloat(e.target.value) || 0)} className="bg-white border-brand-border rounded-lg text-sm" data-testid="max-withdrawal" /></div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
